import { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import { useEffect, useRef, useState } from 'react';
import {
  Animated,
  LayoutChangeEvent,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';

import theme, { getScaledSize } from '../../../themes/theme';
import { Box, Text } from '../Restyle';
import { Skeleton } from '../shared/Skeleton';

export type CustomerTabbarProps = {
  spacing?: number;
  initialRouteName?: string;
  contentContainerStyle?: StyleProp<ViewStyle>;
  tabWidth?: number;
};

type IProps = MaterialTopTabBarProps & CustomerTabbarProps;

const CustomTabBar = ({
  state,
  descriptors,
  navigation,
  spacing = Platform.OS === 'web' ? theme.spacing.m : 10,
  initialRouteName,
  contentContainerStyle,
  tabWidth,
}: IProps): JSX.Element => {
  const indicatorWidthValue = useRef(new Animated.Value(0)).current;
  const indicatorLeftValue = useRef(new Animated.Value(0)).current;
  let isAnimating = false;

  type Position = {
    index: number;
    width: number;
    x: number;
  };

  const [positions, setPositions] = useState<Position[]>([]);

  // get the label widths on mount
  const onLayout = (event: LayoutChangeEvent, index: number, label: string) => {
    const { width, x } = event.nativeEvent.layout;

    setPositions((pos) => {
      const nextPosition = { index, width, x };
      const existingIndex = pos.findIndex((item) => item.index === index);

      if (existingIndex === -1) {
        return [...pos, nextPosition];
      }

      return pos.map((item, itemIndex) =>
        itemIndex === existingIndex ? nextPosition : item
      );
    });

    if (initialRouteName && initialRouteName === label) {
      indicatorWidthValue.setValue(width);
      indicatorLeftValue.setValue(x);
    }

    // TA-10495: previously matched on `state.index > 0`, which re-fired for
    // every label except initialRouteName as each one laid out in turn —
    // the indicator ended up snapping to whichever label happened to lay
    // out last, not the actually-active tab. Match on this label's own
    // index instead.
    if (index === state.index && initialRouteName !== label) {
      indicatorWidthValue.setValue(width);
      indicatorLeftValue.setValue(x);
    }

    if (!initialRouteName && index === 0) {
      indicatorWidthValue.setValue(width);
      indicatorLeftValue.setValue(x);
    }
  };

  useEffect(() => {
    const newPos = positions.findIndex((i) => i.index === state.index);
    if (newPos !== -1 && positions.length > 0) {
      animate(newPos);
    }
  }, [state, positions]);

  const getNewPosX = (position: Position) => {
    if (
      Platform.OS !== 'web' &&
      spacing !== theme.spacing.m &&
      spacing !== theme.spacing.l &&
      position.index !== 0
    ) {
      return position.x - theme.spacing.s * position.index;
    } else {
      return position?.x ?? 0;
    }
  };

  const animate = (newPos: number) => {
    isAnimating = true;
    Animated.parallel([
      Animated.timing(indicatorLeftValue, {
        toValue: getNewPosX(positions[newPos]),
        duration: 100,
        useNativeDriver: false,
      }),
      Animated.timing(indicatorWidthValue, {
        toValue: positions[newPos].width,
        duration: 100,
        useNativeDriver: false,
      }),
    ]).start(() => {
      isAnimating = false;
    });
  };

  // basic labels as suggested by react navigation
  const labels = state.routes.map((route, index) => {
    const { options } = descriptors[route.key];
    const label =
      (options as any).tabBarLabel || (route.params as any)?.tabBarLabel;
    const count =
      (options as any).tabBarCount ?? (route.params as any)?.tabBarCount ?? 0;
    const hasCount = count !== 0 && count !== '' && count !== undefined;
    const countLoading =
      (options as any).tabBarCountLoading ??
      (route.params as any)?.tabBarCountLoading ??
      false;
    const isFocused = state.index === index;

    const onPress = () => {
      if (isAnimating) {
        return;
      }
      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
      });

      if (!isFocused && !event.defaultPrevented) {
        // The `merge: true` option makes sure that the params inside the tab screen are preserved

        // @ts-ignore
        navigation.navigate({ name: route.name, merge: true });
      }
    };

    return (
      <Box
        key={route.key}
        alignItems='center'
        justifyContent='center'
        style={{ marginRight: spacing, width: tabWidth }}
        onLayout={(event) => onLayout(event, index, label)}>
        <TouchableOpacity
          accessibilityRole='button'
          accessibilityState={isFocused ? { selected: true } : {}}
          onPress={onPress}>
          <Box
            accessibilityLabel={label}
            accessibilityValue={
              hasCount && !countLoading ? { text: String(count) } : undefined
            }
            paddingVertical='xs'
            flexDirection='row'
            alignItems='center'>
            <Text
              variant='labelEmphasized'
              color={isFocused ? 'black' : 'grey04'}>
              {label}
            </Text>
            {isFocused && countLoading && (
              <Box marginLeft='xs' testID={`tab-badge-skeleton-${route.name}`}>
                <Skeleton variant='circle' width={20} height={20} />
              </Box>
            )}
            {isFocused && !countLoading && hasCount && (
              <Box
                marginLeft='xs'
                alignItems='center'
                justifyContent='center'
                style={styles.countBadge}>
                <Text variant='webBodyHintSubline' color='white'>
                  {count}
                </Text>
              </Box>
            )}
          </Box>
        </TouchableOpacity>
      </Box>
    );
  });

  return (
    <Box height={getScaledSize(42)} flex={1}>
      <Animated.View style={[styles.container, contentContainerStyle]}>
        {labels}
        <Animated.View
          testID='custom-tab-bar-indicator'
          style={[
            styles.indicator,
            {
              left: indicatorLeftValue,
              width: indicatorWidthValue,
            },
          ]}
        />
      </Animated.View>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    height: getScaledSize(42),
  },
  indicator: {
    backgroundColor: theme.colors.greenSecondary,
    height: 2,
    position: 'absolute',
    bottom: 0,
  },
  countBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    paddingHorizontal: theme.spacing.xxxs,
    backgroundColor: theme.colors.greenSecondary,
  },
});

export default CustomTabBar;
